import { FormikActions } from 'formik';
import React from 'react';
import styled from 'styled-components';
import Form from '../../../app/common/components/Form';
import RadioButtonNormalField from '../../../app/common/fields/RadioButtonNormalField';
import UsernameField from '../../../app/common/fields/UsernameField';
import { ValidatorRule } from '../../../app/common/validator/validatorRule.constants';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { IUpdateUsernameRequest } from './updateUsernameRequest';
import { clearChallengeInput, getChallengeData } from '../../../utils/challengerMain';
import { FormManditoryAstrixMessage } from '../../../app/common/components/FormManitoryMessage';
import { CheckboxWrapper } from '../updatePassword/UpdatePasswordForm';
import CheckboxField from '../../../app/common/fields/CheckboxField';

export interface IUsernameFormValues {
    currentUsername: string;
    username: string;
    signOutFromAllDevices: boolean;
}

export type UpdateUsernameFormProps = IUpdateUsernameFormStateProps &
    IUpdateUsernameFormDispatchProps;

export interface IUpdateUsernameFormStateProps {
    clientId: string;
    isSubmitting: boolean;
    member: any;
    challengerPid: string;
    usernameValidatorRules: ValidatorRule[];
    challengerDegenerate?: boolean;
    sessionRevocationEnabled: boolean;
}

export interface IUpdateUsernameFormDispatchProps {
    updateUsername: (values: IUpdateUsernameRequest, signOutFromAllDevices: boolean) => any;
}

export interface IUpdateUsernameState {
    initialValues: IUsernameFormValues;
    selectedRadioName: string;
    isUsernameChanged: boolean;
}

const BaseWrapper = styled.div`
    margin: 0 !important;
    margin-top: -50px;
`;
const Header = styled.div`
    font-size: 0.75rem;
    line-height: 1;
    font-weight: 500;
    margin: 0;
    margin-bottom: 20px;
`;
const RadioButtonItem = styled.div`
    margin: 0;

    & + & {
        margin-top: 20px;
    }
`;
const UsernameFieldWrapper = styled.div`
    margin: 30px 20px;
    position: relative;
`;

const FieldWrapper = styled.div`
    & > div {
        margin-top: 50px;
        margin-bottom: 40px;
    }
`;

export class UpdateUsernameForm extends React.Component<
    UpdateUsernameFormProps,
    IUpdateUsernameState
> {
    private readonly HEADER: string;
    private readonly ORIGINAL_USERNAME: string;
    private isChallengeLoaded: boolean;

    constructor(props: UpdateUsernameFormProps) {
        super(props);
        this.HEADER = 'label.username.header';

        const { member, challengerPid } = this.props;
        const usernameLowerCase = member.username ? member.username.toLowerCase() : '';
        this.ORIGINAL_USERNAME = usernameLowerCase;

        const initialValues: IUsernameFormValues = {
            currentUsername: usernameLowerCase,
            username: '',
            signOutFromAllDevices: false,
        };
        this.state = {
            initialValues,
            selectedRadioName: 'radio_email',
            isUsernameChanged: usernameLowerCase !== member.email.toLowerCase(),
        };

        this.isChallengeLoaded = !challengerPid;

        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderFormFields = this.renderFormFields.bind(this);
        this.renderExtraFields = this.renderExtraFields.bind(this);
        this.selectedRadio = this.selectedRadio.bind(this);
    }

    public selectedRadio(selectedRadioName: string) {
        this.setState({
            selectedRadioName,
        });
    }

    public render() {
        const { isSubmitting, challengerPid } = this.props;
        const { initialValues, isUsernameChanged } = this.state;

        return (
            <Form
                buttonTextLabel="button.save"
                value={initialValues}
                handleSubmit={this.handleSubmit}
                renderFormFields={this.renderFormFields}
                renderExtraFields={this.renderExtraFields}
                loading={isSubmitting}
                isTargetFieldChanged={isUsernameChanged}
                challengerPid={challengerPid}
                challengerReadyCallback={(isChallengeLoaded: boolean) => {
                    this.isChallengeLoaded = isChallengeLoaded;
                }}
                saveButtonProps={{
                    qaId: 'submit-update-username',
                    width: '100%',
                }}
            />
        );
    }

    handleUsernameChange = (inputString: string) => {
        this.setState({
            isUsernameChanged: this.ORIGINAL_USERNAME.toLowerCase() !== inputString.toLowerCase(),
        });
    };

    private handleSubmit(values: IUsernameFormValues, actions: FormikActions<IUsernameFormValues>) {
        const { clientId, updateUsername, member, challengerPid } = this.props;
        const selectedRadioName = this.state.selectedRadioName;
        const existingEmail = member.email;
        const isRadioEmail = selectedRadioName === 'radio_email';
        const isRadioNotEmail = selectedRadioName === 'radio_not_email';
        const challengeData = getChallengeData(challengerPid);

        if (isRadioEmail && existingEmail) {
            updateUsername(
                {
                    challengeData: getChallengeData(challengerPid),
                    clientId,
                    existingEmail,
                    existingUsername: member.username,
                    username: existingEmail.toLowerCase(),
                },
                values.signOutFromAllDevices,
            );
            actions.resetForm({
                currentUsername: values.currentUsername,
                username: '',
                signOutFromAllDevices: false,
            });
            clearChallengeInput();
        }

        if (isRadioNotEmail && values.username) {
            if (member.username) {
                updateUsername(
                    {
                        challengeData: challengeData,
                        clientId,
                        existingUsername: member.username,
                        username: values.username.toLowerCase(),
                    },
                    values.signOutFromAllDevices,
                );
                actions.resetForm({
                    currentUsername: values.currentUsername,
                    username: values.username.toLowerCase(),
                    signOutFromAllDevices: false,
                });
                clearChallengeInput();
            } else {
                updateUsername(
                    {
                        challengeData: challengeData,
                        clientId,
                        existingEmail: member.email,
                        username: values.username.toLowerCase(),
                    },
                    values.signOutFromAllDevices,
                );
                actions.resetForm({
                    currentUsername: values.currentUsername,
                    username: values.username.toLowerCase(),
                    signOutFromAllDevices: false,
                });
                clearChallengeInput();
            }
        }
    }

    private renderFormFields(values: IUsernameFormValues) {
        const { member, usernameValidatorRules } = this.props;

        const selectedRadioName = this.state.selectedRadioName;

        const header = TranslationHelpers.getFormattedMessage(this.HEADER);
        const isRadioEmail = selectedRadioName === 'radio_email';
        const isRadioNotEmail = selectedRadioName === 'radio_not_email';

        return (
            <FieldWrapper>
                <BaseWrapper>
                    <FieldWrapper>
                        <FormManditoryAstrixMessage />
                    </FieldWrapper>

                    <Header>{header}</Header>
                    <RadioButtonItem>
                        <RadioButtonNormalField
                            id="radio_email"
                            label={TranslationHelpers.getMessageDescriptor('label.radio.email')}
                            value={selectedRadioName === 'radio_email'}
                            qaId={'radio.email'}
                            disabled={false}
                            onCheck={() => {
                                this.selectedRadio('radio_email');
                                this.handleUsernameChange(member.email);
                            }}
                        />
                        {isRadioEmail && (
                            <UsernameFieldWrapper>
                                <UsernameField
                                    id="currentUsername"
                                    labelId="label.current_rak_id"
                                    value={member.email.toLowerCase()}
                                    userEmail={member.email}
                                    disabled={true}
                                    qaId="current-rakuten-id"
                                    validateInput={false}
                                    required={true}
                                />
                            </UsernameFieldWrapper>
                        )}
                    </RadioButtonItem>
                    <RadioButtonItem>
                        <RadioButtonNormalField
                            id="radio_not_email"
                            label={TranslationHelpers.getMessageDescriptor('label.radio.not_email')}
                            value={selectedRadioName === 'radio_not_email'}
                            qaId={'radio.not_email'}
                            disabled={false}
                            onCheck={() => {
                                this.selectedRadio('radio_not_email');
                                this.handleUsernameChange(values.username);
                            }}
                        />
                        {isRadioNotEmail && (
                            <UsernameFieldWrapper>
                                <UsernameField
                                    id="username"
                                    name="username"
                                    labelId="label.new_rak_id"
                                    value={values.username}
                                    userEmail={member.email}
                                    required={true}
                                    onUsernameChange={this.handleUsernameChange}
                                    qaId="new-rakuten-id"
                                    validateInput={true}
                                    validatorRules={usernameValidatorRules}
                                />
                            </UsernameFieldWrapper>
                        )}
                    </RadioButtonItem>
                </BaseWrapper>
            </FieldWrapper>
        );
    }

    private renderExtraFields(values: IUsernameFormValues) {
        const { sessionRevocationEnabled } = this.props;
        return (
            <React.Fragment>
                {sessionRevocationEnabled && (
                    <CheckboxWrapper>
                        <CheckboxField
                            id="signOutFromAllDevices"
                            label="revoke.session.button.text"
                            value={values.signOutFromAllDevices}
                            tooltipText={TranslationHelpers.getMessageDescriptor(
                                'tooltip.revoke_session',
                            )}
                        />
                    </CheckboxWrapper>
                )}
            </React.Fragment>
        );
    }
}
